import { ActionType, getType } from 'typesafe-actions';

import * as actions from './actions';
import {
  ParCouponHistoryEntry,
  PricingUpdateContact,
  PricingUpdatePreview,
  PricingUpdateRun,
  PricingUpdateSchedule,
  PricingUpdateSummary,
  PricingUpdateValuation,
  ValuationCheckStatus,
} from './types';

type PricingUpdatesActionType = ActionType<typeof actions>;

export type DealPricingUpdatesState = {
  schedule: PricingUpdateSchedule | null;
  scheduleLoading: boolean;
  scheduleLoaded: boolean;
  saving: boolean;
  stopping: boolean;
  removingTradeId: number | null;
  sendHistory: PricingUpdateRun[];
  sendHistoryLoading: boolean;
  preview: PricingUpdatePreview | null;
  previewLoading: boolean;
  contacts: PricingUpdateContact[];
  contactsLoading: boolean;
  parCouponHistory: ParCouponHistoryEntry[];
  parCouponHistoryLoading: boolean;
  parCouponHistoryTradeId: number | null;
  summaries: PricingUpdateSummary[];
};

const initialState: DealPricingUpdatesState = {
  schedule: null,
  scheduleLoading: false,
  scheduleLoaded: false,
  saving: false,
  stopping: false,
  removingTradeId: null,
  sendHistory: [],
  sendHistoryLoading: false,
  preview: null,
  previewLoading: false,
  contacts: [],
  contactsLoading: false,
  parCouponHistory: [],
  parCouponHistoryLoading: false,
  parCouponHistoryTradeId: null,
  summaries: [],
};

const patchTrade = (
  schedule: PricingUpdateSchedule | null,
  transaction_id: number,
  patch: Partial<PricingUpdateValuation>,
) =>
  schedule && {
    ...schedule,
    trades: schedule.trades.map((t) =>
      t.transaction_id === transaction_id ? { ...t, ...patch } : t,
    ),
  };

export const dealPricingUpdatesReducer = (
  state: DealPricingUpdatesState = initialState,
  action: PricingUpdatesActionType,
): DealPricingUpdatesState => {
  switch (action.type) {
    case getType(actions.getPricingUpdateSchedule.request):
      return { ...state, scheduleLoading: true };
    case getType(actions.getPricingUpdateSchedule.success):
      return {
        ...state,
        schedule: action.payload.data,
        scheduleLoading: false,
        scheduleLoaded: true,
      };
    case getType(actions.getPricingUpdateSchedule.failure):
      return { ...state, scheduleLoading: false, scheduleLoaded: true };

    case getType(actions.savePricingUpdateSchedule.request):
      return { ...state, saving: true };
    case getType(actions.savePricingUpdateSchedule.success):
      return { ...state, schedule: action.payload.data, saving: false };
    case getType(actions.savePricingUpdateSchedule.failure):
      return { ...state, saving: false };

    case getType(actions.stopPricingUpdateSchedule.request):
      return { ...state, stopping: true };
    case getType(actions.stopPricingUpdateSchedule.success):
      return { ...state, schedule: null, sendHistory: [], stopping: false };
    case getType(actions.stopPricingUpdateSchedule.failure):
      return { ...state, stopping: false };

    case getType(actions.removePricingUpdateTrade.request):
      return { ...state, removingTradeId: action.payload.transaction_id };
    case getType(actions.removePricingUpdateTrade.success):
      return {
        ...state,
        schedule: state.schedule && {
          ...state.schedule,
          trades: state.schedule.trades.filter(
            (t) => t.transaction_id !== state.removingTradeId,
          ),
        },
        removingTradeId: null,
      };
    case getType(actions.removePricingUpdateTrade.failure):
      return { ...state, removingTradeId: null };

    // the row shows as valuing until the live valuation comes back
    case getType(actions.retryPricingUpdateValuation.request):
      return {
        ...state,
        schedule: patchTrade(state.schedule, action.payload.transaction_id, {
          valuation_status: ValuationCheckStatus.Retrying,
        }),
      };
    case getType(actions.retryPricingUpdateValuation.success):
      return {
        ...state,
        schedule: patchTrade(
          state.schedule,
          action.payload.data.transaction_id,
          action.payload.data,
        ),
      };
    case getType(actions.retryPricingUpdateValuation.failure):
      return {
        ...state,
        schedule: patchTrade(state.schedule, action.payload.transaction_id, {
          valuation_status: ValuationCheckStatus.Failed,
        }),
      };

    case getType(actions.getPricingUpdateSendHistory.request):
      return { ...state, sendHistoryLoading: true };
    case getType(actions.getPricingUpdateSendHistory.success):
      return {
        ...state,
        sendHistory: action.payload.data,
        sendHistoryLoading: false,
      };
    case getType(actions.getPricingUpdateSendHistory.failure):
      return { ...state, sendHistoryLoading: false };

    case getType(actions.getPricingUpdatePreview.request):
      return { ...state, preview: null, previewLoading: true };
    case getType(actions.getPricingUpdatePreview.success):
      return {
        ...state,
        preview: action.payload.data,
        previewLoading: false,
      };
    case getType(actions.getPricingUpdatePreview.failure):
      return { ...state, previewLoading: false };

    case getType(actions.getPricingUpdateContacts.request):
      return { ...state, contactsLoading: true };
    case getType(actions.getPricingUpdateContacts.success):
      return {
        ...state,
        contacts: action.payload.data,
        contactsLoading: false,
      };
    case getType(actions.getPricingUpdateContacts.failure):
      return { ...state, contactsLoading: false };

    case getType(actions.getParCouponHistory.request):
      return {
        ...state,
        parCouponHistory: [],
        parCouponHistoryLoading: true,
        parCouponHistoryTradeId: action.payload.transaction_id,
      };
    case getType(actions.getParCouponHistory.success):
      return {
        ...state,
        parCouponHistory: action.payload.data,
        parCouponHistoryLoading: false,
      };
    case getType(actions.getParCouponHistory.failure):
      return { ...state, parCouponHistoryLoading: false };

    case getType(actions.getPricingUpdateSummaries.success):
      return { ...state, summaries: action.payload.data };

    // the tile summaries belong to the Deals list, not the open deal
    case getType(actions.resetPricingUpdates):
      return { ...initialState, summaries: state.summaries };

    case getType(actions.resetPricingUpdateSummaries):
      return initialState;

    default:
      return state;
  }
};

export default dealPricingUpdatesReducer;
